import React, { useState, useContext, useEffect, useRef } from 'react';

import ContextMenu from '../global/ContextMenu';
import MiniModal from  '@/components/modals/MiniModal';
import MoreDetails from './MoreDetails';
import FilterHeader from '../global/FilterHeader';
import useOnScreen from  '@/components/hooks/useOnScreen';

import { LockerManagementContext } from '@/context/LockerManagmentContext';
import { ToastMessageType } from  '@/components/elements/global/ToastNotification';
import { BuildPath } from  '@/helpers/Paths';
import { LockerContextRefreshType as Table, ModalProps } from  '@/interfaces/LockerManagement';
import { container, TYPES } from  '@/inversifyconfig/container.config';
import { IHttpRequest } from  '@/interfaces/IHttp';
import { download, getFileTimeStamp } from  '@/helpers/Files';

import NotFound from './NotFound';

import { Truncate } from  '@/helpers/Text';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { IconLibrary, ThriveIcon } from 'thrive-components';



const BatchManagementTable = ({setToast}:{setToast: (messageType: ToastMessageType, message: JSX.Element) => void}) => {
    /* Ref for infinite scrolling, if there are more files, load them and append to files when this comes into view */
    const scrollTo = useRef();
    const isVisible = useOnScreen(scrollTo, document.querySelector('.table-wrapper.batch'));

    const [showModal, setShowModal] = useState<boolean>(false);
    const [modalContents, setModalContents] = useState<ModalProps>();
    const { batches, nextSkip, refreshData, nextPage, isLoading, searchAndFilter, searchParams} = useContext(LockerManagementContext);
    const httpCall = container.get<IHttpRequest>(TYPES.IHttpRequest);  

    useEffect(()=> {
        return () => {
            scrollTo.current = undefined;
        }
    },[])

    useEffect(()=>{
        isVisible && batches.length && (batches.length === nextSkip) && nextPage(Table.BATCHES);
    },[isVisible, nextSkip])
    
    function generateContextOptions(fileName: string, id: number) {
        return [
            {
                label: 'More Details',
                onClick: () => showMoreDetailsModal(fileName, id)
            },
            {
                label:'Archive Batch',
                onClick: () => showArchiveModal(fileName, id)
            }, 
        ]
    }

    function TableRows(): JSX.Element  {
        return (
        <>
            {batches.map((batch, idx) => (
                <tr key={`table-row-${idx}`}>
                    <td>
                        <span className="lockerfile-batchname">
                            <img src={`${BuildPath()}/images/locker/fileIcons.svg`} alt="batch icon"/>
                            {batch.batchName.length >= 18 &&
                            <ReactTooltip id={`tooltip-batchName-${batch.batchId}`} place="top" className='thrive-tooltip no-width'  aria-hidden="true">
                                <span>{batch.batchName}</span>
                            </ReactTooltip> 
                            }
                            <span data-tooltip-id={`tooltip-batchName-${batch.batchId}`}>
                                {Truncate(batch.batchName, 18)}
                            </span>
                        </span>
                    </td>
                    <td>{new Date(batch.lastUpdate).toLocaleDateString('en-gb')}</td>
                    <td>{batch.nonArchivedFiles}</td>
                    <td>{batch.nonArchivedUsers}</td>
                    <td className="more-details-button-wrapper">
                        <ContextMenu
                            className="more-details-button"
                            contextLabel={`more details - ${batch.batchName}`}
                            options={generateContextOptions(batch.batchName, batch.batchId)}
                            closeOnScrollSelector={'.table-wrapper'}
                        />
                    </td>
                </tr>
            ))}
        </>
        );
    }

    function archiveFile(fileName: string, id: number) {
        httpCall.put(`locker/batches/${id}/archive`, {isArchived: true})
        .then(() => setToast(ToastMessageType.SUCCESS, <><span className="emphasised-text">{Truncate(fileName, 50)}&nbsp;</span> has been successfully archived</>))
        .catch(() => setToast(ToastMessageType.WARNING, <><span className="emphasised-text">{Truncate(fileName, 50)}&nbsp;</span> could not be archived at this time</>))
        .finally(()=> {
            refreshData(Table.BATCHES);
            setShowModal(false);
        })
    }
   

    function showArchiveModal (fileName: string, id: number) {
        setShowModal(true);
        setModalContents({
            title: `Archive Batch`,
            confirmLabel: `Yes, Archive Files`,
            onClose: () => setShowModal(false),
            onConfirm: () => archiveFile(fileName, id),
            contents: (
                <>
                    <h3 className="archive-sub-heading">
                        Are you sure you want to archive

                        <span className="emphasis-text">
                            {Truncate(fileName, 50)}
                            ?
                        </span>
                    </h3>
                    
                    <div className="archive-warning-wrapper">
                        <ThriveIcon 
                            className="archive-warning-icon"
                            iconName={IconLibrary.ALERT_INFO}
                            sizeInPx='12'
                            color='var(--thriveRed)'
                        />

                        <p className="archive-warning-text warning-text">
                           By archiving a batch you will archive all associated batch files and remove these files from a user's Locker
                        </p>
                    </div>
                </>
            )
        });
    }

    function showMoreDetailsModal (fileName: string, id: number) {
        setShowModal(true);
        setModalContents({
            title: `More Details: ${Truncate(fileName, 50)}`,
            confirmLabel: 'Download Full Report',
            cancelLabel: 'Close',
            onClose: () => setShowModal(false),
            onConfirm: () => {
                download(
                    `../../api/locker/batches/${id}/details/download`,
                    `${fileName}_MoreDetailsReport_${getFileTimeStamp()}.csv`
                );
            },
            contents: <MoreDetails id={id} type="batch" />
        })
    }
    
    return (
    <>
        <div className='anti-floating-text-div' />
        <div className={`table-wrapper batch narrow-scroll-bar`}>
            <table className={`batches ${batches.length > 13 ? 'scroll-table': ''}`}>
                <thead>
                    <tr>
                        <FilterHeader 
                            columnNames={["Batch ID", "Last Update", "Files", "Users"]}
                            setFilter={(filter) => searchAndFilter(Table.BATCHES, filter)}
                            currentFilters={searchParams[Table.BATCHES]}
                        />
                        <th>Details</th>
                    </tr> 
                </thead>
                <tbody className={isLoading ? ' loading' : ''}>
                    {!!batches.length &&  <TableRows />}
                </tbody>
            </table>
            <div className={`next-page-wrapper`} ref={scrollTo} />
            {!batches.length &&
                <NotFound message="You have no locker files" table={Table.BATCHES}/>
            }
            {showModal &&
                <MiniModal 
                    className='larger-mini-modal locker-management-modal'
                    showModal={showModal}
                    {...modalContents}
                >
                    {modalContents?.contents}
                </MiniModal>
            }
        </div>
    </>
       
)}

export default BatchManagementTable